import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  Plus, Edit, Trash2, ExternalLink, Image, Upload, GripVertical,
  Link as LinkIcon, Palette, Type, Power, Eye, EyeOff, Globe, 
  ArrowUp, ArrowDown, X, Save
} from "lucide-react";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "");

export default function AccesosPortalesPage() {
  const [accesos, setAccesos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [reordenando, setReordenando] = useState(false);
  const [ordenModificado, setOrdenModificado] = useState(false);
  
  const [formData, setFormData] = useState({
    nombre: '',
    url: '',
    imagen_url: '',
    color_fondo: '#3b82f6',
    color_texto: '#ffffff',
    activo: true,
    orden: 0
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const cargarAccesos = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/accesos-portales`, getAuthHeaders());
      setAccesos(response.data);
      setOrdenModificado(false);
    } catch (error) {
      toast.error("Error al cargar accesos");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarAccesos();
  }, [cargarAccesos]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/api/accesos-portales/imagen`,
        formDataUpload,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setFormData(prev => ({ ...prev, imagen_url: response.data.imagen_url }));
      toast.success("Imagen subida correctamente");
    } catch (error) {
      toast.error("Error al subir imagen");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleEliminarImagen = () => {
    setFormData(prev => ({ ...prev, imagen_url: '' }));
    toast.success("Imagen eliminada");
  };

  const handleGuardar = async () => {
    if (!formData.nombre || !formData.url) {
      toast.error("Nombre y URL son requeridos");
      return;
    }

    try {
      if (editando) {
        await axios.put(
          `${API_URL}/api/accesos-portales/${editando}`,
          formData,
          getAuthHeaders()
        );
        toast.success("Acceso actualizado");
      } else {
        await axios.post(
          `${API_URL}/api/accesos-portales`,
          { ...formData, orden: accesos.length },
          getAuthHeaders()
        );
        toast.success("Acceso creado");
      }
      
      setDialogOpen(false);
      resetForm();
      cargarAccesos();
    } catch (error) {
      toast.error("Error al guardar");
      console.error(error);
    }
  };

  const handleEditar = (acceso) => {
    setEditando(acceso.id);
    setFormData({
      nombre: acceso.nombre || '',
      url: acceso.url || '',
      imagen_url: acceso.imagen_url || '',
      color_fondo: acceso.color_fondo || '#3b82f6',
      color_texto: acceso.color_texto || '#ffffff',
      activo: acceso.activo ?? true,
      orden: acceso.orden || 0
    });
    setDialogOpen(true);
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar este acceso?")) return;
    
    try {
      await axios.delete(`${API_URL}/api/accesos-portales/${id}`, getAuthHeaders());
      toast.success("Acceso eliminado");
      cargarAccesos();
    } catch (error) {
      toast.error("Error al eliminar");
      console.error(error);
    }
  };

  const handleToggle = async (id) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/accesos-portales/${id}/toggle`,
        {},
        getAuthHeaders()
      );
      toast.success(response.data.activo ? "Acceso activado" : "Acceso desactivado");
      cargarAccesos();
    } catch (error) {
      toast.error("Error al cambiar estado");
      console.error(error);
    }
  };

  const moverAcceso = (index, direccion) => {
    if (index + direccion < 0 || index + direccion >= accesos.length) return;
    
    const nuevosAccesos = [...accesos];
    const temp = nuevosAccesos[index];
    nuevosAccesos[index] = nuevosAccesos[index + direccion];
    nuevosAccesos[index + direccion] = temp;
    
    // Actualizar el orden
    nuevosAccesos.forEach((a, i) => {
      a.orden = i;
    });
    
    setAccesos(nuevosAccesos);
    setOrdenModificado(true);
  };

  const guardarOrden = async () => {
    try {
      setReordenando(true);
      const ordenes = accesos.map((a, i) => ({ id: a.id, orden: i }));
      await axios.post(
        `${API_URL}/api/accesos-portales/reordenar`,
        { ordenes },
        getAuthHeaders()
      );
      toast.success("Orden guardado correctamente");
      setOrdenModificado(false);
      cargarAccesos();
    } catch (error) {
      toast.error("Error al guardar orden");
      console.error(error);
    } finally {
      setReordenando(false);
    }
  };

  const resetForm = () => {
    setEditando(null);
    setFormData({
      nombre: '',
      url: '',
      imagen_url: '',
      color_fondo: '#3b82f6',
      color_texto: '#ffffff',
      activo: true,
      orden: 0
    });
  };

  const handleNuevo = () => {
    resetForm();
    setDialogOpen(true);
  };

  // Función para construir la URL correcta de la imagen
  const getImageUrl = (imagen_url) => {
    if (!imagen_url) return null;
    if (imagen_url.startsWith('http')) return imagen_url;
    return `${API_URL}${imagen_url}`;
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="ml-0 md:ml-64 flex-1 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-3">
              <Globe className="w-8 h-8 text-blue-600" />
              Accesos a Portales
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-2">
              Configure botones de acceso rápido a portales externos que aparecerán en el dashboard de todos los usuarios
            </p>
          </div>

          {/* Main Card */}
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="dark:text-white">Botones de Acceso Configurados</CardTitle>
                <CardDescription className="dark:text-slate-400">
                  {accesos.length} acceso(s) configurado(s) • {accesos.filter(a => a.activo).length} activo(s)
                </CardDescription>
              </div>
              <div className="flex gap-2">
                {ordenModificado && (
                  <Button 
                    onClick={guardarOrden} 
                    disabled={reordenando}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {reordenando ? "Guardando..." : "Guardar Orden"}
                  </Button>
                )}
                <Button onClick={handleNuevo} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Acceso
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-slate-500">Cargando...</div>
              ) : accesos.length === 0 ? (
                <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                  <Globe className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No hay accesos configurados</p>
                  <p className="text-sm">Crea tu primer acceso a portal</p>
                </div>
              ) : (
                <>
                  {ordenModificado && (
                    <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center gap-2 text-amber-700 dark:text-amber-400">
                      <GripVertical className="w-4 h-4" />
                      <span className="text-sm">Has modificado el orden. Haz clic en "Guardar Orden" para aplicar los cambios.</span>
                    </div>
                  )}
                  <Table>
                    <TableHeader>
                      <TableRow className="dark:border-slate-700">
                        <TableHead className="dark:text-slate-300 w-20">Orden</TableHead>
                        <TableHead className="dark:text-slate-300">Vista Previa</TableHead>
                        <TableHead className="dark:text-slate-300">Nombre</TableHead>
                        <TableHead className="dark:text-slate-300">URL</TableHead>
                        <TableHead className="dark:text-slate-300">Estado</TableHead>
                        <TableHead className="dark:text-slate-300 text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accesos.map((acceso, index) => (
                        <TableRow key={acceso.id} className="dark:border-slate-700">
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => moverAcceso(index, -1)}
                                disabled={index === 0}
                                title="Subir"
                              >
                                <ArrowUp className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => moverAcceso(index, 1)}
                                disabled={index === accesos.length - 1}
                                title="Bajar"
                              >
                                <ArrowDown className="w-4 h-4" />
                              </Button>
                              <span className="text-xs text-slate-400 ml-1">{index + 1}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div 
                              className="px-4 py-2 rounded-lg inline-flex items-center gap-2 text-sm font-medium"
                              style={{ 
                                backgroundColor: acceso.color_fondo, 
                                color: acceso.color_texto 
                              }}
                            >
                              {acceso.imagen_url && (
                                <img 
                                  src={getImageUrl(acceso.imagen_url)}
                                  alt=""
                                  className="w-5 h-5 object-contain"
                                  style={{ background: 'transparent' }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              {acceso.nombre}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium dark:text-white">{acceso.nombre}</TableCell>
                          <TableCell className="dark:text-slate-300">
                            <a 
                              href={acceso.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 flex items-center gap-1"
                            >
                              {acceso.url.length > 40 ? acceso.url.substring(0, 40) + '...' : acceso.url}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={acceso.activo}
                                onCheckedChange={() => handleToggle(acceso.id)}
                              />
                              <Badge variant={acceso.activo ? "default" : "secondary"}>
                                {acceso.activo ? "Activo" : "Inactivo"}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => handleEditar(acceso)}
                                title="Editar"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => handleEliminar(acceso.id)}
                                className="text-red-500 hover:text-red-700"
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>

          {/* Dialog para crear/editar */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent className="max-w-lg dark:bg-slate-800 dark:border-slate-700">
              <DialogHeader>
                <DialogTitle className="dark:text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  {editando ? "Editar Acceso a Portal" : "Nuevo Acceso a Portal"}
                </DialogTitle>
                <DialogDescription className="dark:text-slate-400">
                  Configure el botón de acceso que aparecerá en el dashboard
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Nombre */}
                <div className="space-y-2">
                  <Label className="dark:text-slate-300 flex items-center gap-2">
                    <Type className="w-4 h-4" />
                    Nombre del Botón *
                  </Label>
                  <Input
                    value={formData.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    placeholder="Ej: Portal Aseguradora"
                    className="dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                  />
                </div>

                {/* URL */}
                <div className="space-y-2">
                  <Label className="dark:text-slate-300 flex items-center gap-2">
                    <LinkIcon className="w-4 h-4" />
                    URL del Portal *
                  </Label>
                  <Input
                    value={formData.url}
                    onChange={(e) => handleInputChange('url', e.target.value)}
                    placeholder="https://..."
                    className="dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                  />
                </div>

                {/* Imagen */}
                <div className="space-y-2">
                  <Label className="dark:text-slate-300 flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Imagen/Ícono (opcional)
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.imagen_url}
                      onChange={(e) => handleInputChange('imagen_url', e.target.value)}
                      placeholder="URL de imagen o subir archivo"
                      className="flex-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <Button type="button" variant="outline" disabled={uploading} asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-1" />
                          {uploading ? "Subiendo..." : "Subir"}
                        </span>
                      </Button>
                    </label>
                  </div>
                  {formData.imagen_url && (
                    <div className="flex items-center gap-2 p-2 bg-slate-100 dark:bg-slate-700 rounded">
                      <div 
                        className="w-10 h-10 rounded flex items-center justify-center"
                        style={{ backgroundColor: formData.color_fondo }}
                      >
                        <img 
                          src={getImageUrl(formData.imagen_url)}
                          alt="Preview"
                          className="w-8 h-8 object-contain"
                          style={{ background: 'transparent' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                      <span className="text-xs text-slate-500 dark:text-slate-400 truncate flex-1">
                        {formData.imagen_url}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        onClick={handleEliminarImagen}
                        title="Eliminar imagen"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Recomendación: Use imágenes con fondo transparente (PNG) para mejor visualización
                  </p>
                </div>

                {/* Colores */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="dark:text-slate-300 flex items-center gap-2">
                      <Palette className="w-4 h-4" />
                      Color de Fondo
                    </Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.color_fondo}
                        onChange={(e) => handleInputChange('color_fondo', e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={formData.color_fondo}
                        onChange={(e) => handleInputChange('color_fondo', e.target.value)}
                        className="flex-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="dark:text-slate-300 flex items-center gap-2">
                      <Type className="w-4 h-4" />
                      Color del Texto
                    </Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.color_texto}
                        onChange={(e) => handleInputChange('color_texto', e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={formData.color_texto}
                        onChange={(e) => handleInputChange('color_texto', e.target.value)}
                        className="flex-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Estado activo */}
                <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Power className="w-4 h-4 text-slate-500" />
                    <Label className="dark:text-slate-300">Activo</Label>
                  </div>
                  <Switch
                    checked={formData.activo}
                    onCheckedChange={(checked) => handleInputChange('activo', checked)}
                  />
                </div>

                {/* Vista previa */}
                <div className="p-4 bg-slate-100 dark:bg-slate-700 rounded-lg">
                  <Label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">Vista Previa</Label>
                  <button
                    type="button"
                    className="px-4 py-3 rounded-lg flex items-center gap-3 font-medium transition-all hover:opacity-90 shadow-sm"
                    style={{ 
                      backgroundColor: formData.color_fondo, 
                      color: formData.color_texto 
                    }}
                  >
                    {formData.imagen_url && (
                      <img 
                        src={getImageUrl(formData.imagen_url)}
                        alt=""
                        className="w-6 h-6 object-contain"
                        style={{ background: 'transparent' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    {formData.nombre || "Nombre del botón"}
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleGuardar} className="bg-blue-600 hover:bg-blue-700">
                  {editando ? "Guardar Cambios" : "Crear Acceso"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
}
